import type { Row } from '@solum/sdk'
import type { SelectOption } from '@solum/ui'

const titleCase = (v: string) => v.charAt(0).toUpperCase() + v.slice(1)

/**
 * Is this role-type row available for a NEW assignment? (#1075)
 *
 * `status == null` is the pre-2026090121 shape and reads as available: an older
 * core that does not answer the question must not have its silence read as
 * "archived", which would empty the picker on every deployment behind the
 * migration.
 */
export const isRoleTypeAvailable = (roleType: Row): boolean =>
  roleType.status == null || roleType.status === 'active'

/** The label a role key renders as, from the UNFILTERED catalogue. */
export const roleTypeLabel = (roleType: Row | undefined, roleKey: string): string =>
  String(roleType?.label ?? titleCase(roleKey))

/**
 * The organisation-role choices, as ONE array carrying its own availability.
 *
 * This face consumes the same availability contract the generic picker does
 * (`selectable`) rather than maintaining a second filter rule of its own — the
 * ruling's clause, and the reason is that two rules drift. Before #1075 this
 * function DROPPED archived types; dropping is what blanks a label, so an
 * archived type is now returned with `selectable: false` and the control
 * renders it disabled with an Archived marker.
 *
 * Two things are still genuinely filtered out, and neither is an availability
 * question:
 *   · a role the organisation ALREADY wears actively — offering it again is not
 *     a retired vocabulary entry, it is a duplicate;
 *   · a role whose declared audience is `person` — `applies_to` is advisory
 *     about WHO can wear it (0022), a different axis from whether the
 *     definition is still open.
 */
export function availableOrganizationRoles(roleTypes: Row[], roles: Row[]): SelectOption[] {
  const active = new Set(roles.filter((r) => r.status === 'active').map((r) => String(r.role)))
  return roleTypes
    .filter((r) => Boolean(r.is_commercial)
      && (r.applies_to === 'organization' || r.applies_to === 'any')
      && !active.has(String(r.key)))
    .sort((a, b) => Number(a.sort ?? 0) - Number(b.sort ?? 0))
    .map((r) => ({
      value: String(r.key),
      label: roleTypeLabel(r, String(r.key)),
      selectable: isRoleTypeAvailable(r),
    }))
}

/** Reactivate history where possible instead of manufacturing duplicate role rows. */
export function organizationRoleAssignment(roles: Row[], partyId: string, role: string, ownerId: string) {
  const inactive = roles.find((r) => r.role === role && r.status === 'inactive')
  return inactive
    ? { id: String(inactive.id), patch: { status: 'active', valid_to: null } as Row }
    : {
        id: 'new',
        patch: {
          owner_id: ownerId, party_type: 'core_organization', party_id: partyId,
          role, status: 'active',
        } as Row,
      }
}
